import { Loader2, Coffee } from 'lucide-react'
import type { Language } from '@/types'

interface LoadingProps {
  message?: string
  language?: Language
}

export function Loading({ message, language = 'fr' }: LoadingProps) {
  const defaultMessage =
    language === 'fr' ? 'Chargement...' : language === 'ar' ? 'جاري التحميل...' : 'Loading...'

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center">
        <div className="relative w-20 h-20 mx-auto mb-5 flex items-end justify-center">
          {/* Steam wisps */}
          <div className="absolute top-0 left-1/2 -translate-x-3 flex gap-2.5">
            <span className="block w-[3px] h-4 rounded-full bg-primary/30 animate-steam" />
            <span
              className="block w-[3px] h-4 rounded-full bg-primary/30 animate-steam"
              style={{ animationDelay: '0.5s' }}
            />
          </div>
          {/* Cup */}
          <Coffee size={40} className="text-primary" strokeWidth={1.6} />
        </div>
        <p className="font-heading text-lg text-primary/80">{message || defaultMessage}</p>
      </div>
    </div>
  )
}

// ============================================================================
// Loading Skeleton (for cards)
// ============================================================================

interface LoadingSkeletonProps {
  count?: number
}

export function LoadingSkeleton({ count = 6 }: LoadingSkeletonProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card flex sm:flex-col">
          <div className="w-24 sm:w-full aspect-square sm:aspect-[4/3] skeleton shrink-0" />
          <div className="flex-1 p-3 sm:p-4 space-y-2.5">
            <div className="h-5 skeleton rounded w-3/4" />
            <div className="h-4 skeleton rounded w-full" />
            <div className="h-4 skeleton rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Loading Spinner (inline)
// ============================================================================

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function LoadingSpinner({ size = 'md', className = '' }: LoadingSpinnerProps) {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  }

  return (
    <div className={`${sizes[size]} ${className}`}>
      <Loader2 className="w-full h-full animate-spin text-primary" />
    </div>
  )
}

// ============================================================================
// Loading Overlay (full screen overlay)
// ============================================================================

interface LoadingOverlayProps {
  message?: string
}

export function LoadingOverlay({ message }: LoadingOverlayProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="text-center">
        <div className="relative w-20 h-20 mx-auto mb-4 flex items-end justify-center">
          <div className="absolute top-0 left-1/2 -translate-x-3 flex gap-2.5">
            <span className="block w-[3px] h-4 rounded-full bg-primary/30 animate-steam" />
            <span
              className="block w-[3px] h-4 rounded-full bg-primary/30 animate-steam"
              style={{ animationDelay: '0.5s' }}
            />
          </div>
          <Coffee size={40} className="text-primary" strokeWidth={1.6} />
        </div>
        {message && <p className="font-heading text-lg text-primary/80">{message}</p>}
      </div>
    </div>
  )
}
